// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import NonTranslationTypography from "@sc/App/Components/NonTranslationTypography/NonTranslationTypography";
import { XNGAGE_CUSTOM_WIDGET } from "@sc/XDL/constants";
import ArrowRight from "@sc/XDL/Icons/ArrowRight";
import { color, createLinkIconStyles, xScreen } from "@sc/XDL/Utilities";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    session: getSession(state),
    myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
    customerNumber: getCurrentBillToState(state)?.value?.customerNumber,
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & HasHistory;

export interface WelcomingWidgetStyles {
    link?: LinkPresentationProps;
    wrapper?: InjectableCss;
    sectionWrapper?: InjectableCss;
    welcomeMsg?: TypographyPresentationProps;
    accountNumber?: TypographyPresentationProps;
}

export const welcomingWidgetStyles: WelcomingWidgetStyles = {
    wrapper: {
        css: css`
            padding-bottom: 8px;
            border-bottom: 1px solid ${color("common.border")};
            width: 100%;
            display: flex;
            row-gap: 12px;
            align-items: flex-end;
            ${xScreen("lte", "md")} {
                padding-bottom: 12px;
                flex-wrap: wrap;
            }
        `,
    },
    sectionWrapper: {
        css: css`
            display: flex;
            column-gap: 32px;
            row-gap: 8px;
            flex-wrap: wrap;
            align-items: flex-end;
            ${xScreen("lte", "sm")} {
                flex-direction: column;
                align-items: flex-start;
            }
        `,
    },
    link: {
        ...createLinkIconStyles(ArrowRight, {
            icon: {
                position: "right",
                iconProps: {
                    css: css`
                        &,
                        svg {
                            width: 11px;
                        }
                    `,
                },
            },
            css: css`
                margin-left: auto;
                margin-bottom: 4px;
                ${xScreen("lte", "md")} {
                    width: 100%;
                    margin-left: 0;
                    justify-content: flex-end;
                    margin-bottom: 0;
                }
            `,
        }),
    },
    welcomeMsg: {
        variant: "h2",
        forwardAs: "p",
        css: css`
            margin-bottom: 0;
        `,
    },
    accountNumber: {
        variant: "h4",
        forwardAs: "p",
        css: css`
            margin-bottom: 2px;
            ${xScreen("lte", "sm")} {
                margin-bottom: 0;
            }
        `,
    },
};

const WelcomingWidget: FC<Props> = ({ session, history, myAccountPageUrl, customerNumber }) => {
    const styles = welcomingWidgetStyles;
    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
            return;
        }
        history.push("/MyAccount");
    };
    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.sectionWrapper}>
                <Typography {...styles.welcomeMsg}>
                    {`${translate("Hello")}, `}
                    <NonTranslationTypography>{session?.firstName}</NonTranslationTypography>
                </Typography>
                {customerNumber && (
                    <Typography {...styles.accountNumber}>
                        {`${translate("Account #:")} `}
                        <NonTranslationTypography>{customerNumber}</NonTranslationTypography>
                    </Typography>
                )}
            </StyledWrapper>

            <Link aria-label={translate("View My Dashboard")} {...styles.link} onClick={viewMyDashboardClickHandler}>
                {translate("View My Dashboard")}
            </Link>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withHistory(WelcomingWidget)),
    definition: {
        displayName: "Welcome",
        group: XNGAGE_CUSTOM_WIDGET,
    },
};

export default widgetModule;
